// tslint:disable triple-equals
import React, { useEffect, useState } from 'react';
import {
  NotificationPreferencesWrapper,
  SwitchContainer,
  Toggle,
  SwitchWrapper,
  PreferencesCheckboxWrapper,
  CheckboxesWrapper,
  PreferencesMainContainer,
  SkeletonLoaderNotificationsWrapper,
  ProfileActionCardTitle,
} from './NotificationPreferences.styled';
import { Checkbox } from '../Checkbox';
import { BellSvgIcon } from './BellSvgIcon';
import {
  getNotificationPreferences,
  putNotificationPreferences,
} from '../../../../requests/profile';
import { showSuccessMessage } from '../../../../helpers/showMessage';
import { sentryReport } from '../../../../helpers/sentry';
import { SkeletonLoader } from '../../sharedStyledComponents/SkeletonLoader';

export interface NotificationPreferencesResponse {
  id: number;
  userId: number;
  notificationType: string; // "email" | "pushNotification" | "sms" | "inAppMessaging"
  insertDate: string;
  updateDate: string;
  status: boolean;
  label: string; // "E-mail" | "Push Notification" | "Text Message (SMS)" | "In-App Messaging"
}

export interface NotificationPreferencesPayload {
  email?: boolean;
  inApp?: boolean;
  sms?: boolean;
}

export function NotificationPreferences () {
  const [isLoadingPreferences, setIsLoadingPreferences] =
    useState<boolean>(false);
  const [allPreferences, setallPreferences] = useState<
    NotificationPreferencesResponse[]
  >([{}] as NotificationPreferencesResponse[]);

  function updateNotificationPreferences (notificationId: number) {
    const updatePreference = allPreferences.map((preference) =>
      preference.id === notificationId
        ? { ...preference, status: !preference.status }
        : preference
    ) as NotificationPreferencesResponse[];
    setallPreferences(updatePreference);

    reflectNotificationPreferences(updatePreference);
  }

  function reflectNotificationPreferences (
    updatePreference: NotificationPreferencesResponse[]
  ) {
    const preferencesPayload: NotificationPreferencesPayload =
      updatePreference.reduce((accumulator, currentValue) => {
        accumulator[currentValue.notificationType] = currentValue.status;
        return accumulator;
      }, {});

    if (!preferencesPayload.email && !preferencesPayload.inApp) {
      toggleAllOff();
      return;
    }

    setIsLoadingPreferences(true);
    putNotificationPreferences(preferencesPayload)
      .then(() => {
        showSuccessMessage('Your notification preferences were updated!');
      })
      .catch((error) => {
        loadPreferences();
        showSuccessMessage('Unable to update your notification preferences.');
      })
      .finally(() => {
        setIsLoadingPreferences(false);
      });
  }

  function loadPreferences () {
    setIsLoadingPreferences(true);
    getNotificationPreferences()
      .then((response) => {
        const temporaryPreferences =
          response.data.user.notificationSettings.map((item) => {
            if (item.notificationType == 'email') {
              return { ...item, label: 'E-mail' };
            }
            if (item.notificationType == 'inApp') {
              return { ...item, label: 'Push Notification' };
            }
            if (item.notificationType == 'sms') {
              return { ...item, label: 'Text Message (SMS)' };
            }
          });
        setallPreferences(temporaryPreferences);
      })
      .catch((error) => {
        sentryReport(error.response.data, error.message, error.code, {
          path: '/pilotwelcome',
          get: 'getNotificationPreferences',
        });
      })
      .finally(() => {
        setIsLoadingPreferences(false);
      });
  }

  useEffect(() => {
    loadPreferences();
  }, []);

  function toggleAllOff () {
    setIsLoadingPreferences(true);
    putNotificationPreferences({
      email: isToggleOn === true && false,
      sms: isToggleOn === true && false,
      inApp: isToggleOn === true ? false : true,
    })
      .then(() => {
        loadPreferences();
        showSuccessMessage('Your notification preferences were updated!');
      })
      .catch((error) => {
        loadPreferences();
        showSuccessMessage('Unable to update your notification preferences.');
        sentryReport(error.response.data, error.message, error.code, {
          path: '/pilotwelcome',
          get: 'putNotificationPreferences',
        });
      })
      .finally(() => {
        setIsLoadingPreferences(false);
      });
  }

  const [isToggleOn, setIsToggleOn] = useState<boolean>(false);

  useEffect(() => {
    if (allPreferences.length > 0) {
      setIsToggleOn(
        allPreferences && allPreferences.some((preference) => preference.status)
      );
    }
  }, [allPreferences]);

  return (
    <>
      <NotificationPreferencesWrapper>
        <PreferencesCheckboxWrapper>
          <BellSvgIcon />
          <PreferencesMainContainer>
            <ProfileActionCardTitle>
              Notification Preferences
            </ProfileActionCardTitle>

            {isToggleOn && !isLoadingPreferences && (
              <CheckboxesWrapper>
                {allPreferences
                  .filter((preference) => preference.notificationType !== 'sms')
                  .map((preference) => (
                    <Checkbox
                      key={preference.id}
                      label={preference.label}
                      onClick={() =>
                        updateNotificationPreferences(preference.id)
                      }
                      checked={preference.status}
                      disabled={false}
                    />
                  ))}
              </CheckboxesWrapper>
            )}

            {isLoadingPreferences && (
              <SkeletonLoaderNotificationsWrapper>
                <SkeletonLoader width="142px" height="24px" />
                <SkeletonLoader width="142px" height="24px" />
              </SkeletonLoaderNotificationsWrapper>
            )}
          </PreferencesMainContainer>
        </PreferencesCheckboxWrapper>
        <SwitchWrapper>
          {!isLoadingPreferences ? (
            <SwitchContainer
              isOn={isToggleOn}
              onClick={() => {
                setIsToggleOn(!isToggleOn);
                toggleAllOff();
              }}
            >
              <Toggle isOn={isToggleOn} />
            </SwitchContainer>
          ) : (
            <SkeletonLoader width="56px" height="30px" />
          )}
        </SwitchWrapper>
      </NotificationPreferencesWrapper>
    </>
  );
}
